import React, { useEffect, useState } from 'react';
import { Megaphone, Loader2, Plus, Trash2, ToggleLeft, ToggleRight, Info, AlertTriangle, Wrench, Sparkles } from 'lucide-react';
import { useAdminStore, SystemAnnouncement } from '../../store/adminStore';

const TYPE_CONFIG: Record<string, { icon: React.ElementType; label: string; cls: string }> = {
  info: { icon: Info, label: 'Information', cls: 'bg-info/10 text-info border-info/20' },
  warning: { icon: AlertTriangle, label: 'Avertissement', cls: 'bg-warning/10 text-warning border-warning/20' },
  maintenance: { icon: Wrench, label: 'Maintenance', cls: 'bg-error/10 text-error border-error/20' },
  update: { icon: Sparkles, label: 'Mise à jour', cls: 'bg-success/10 text-success border-success/20' },
};

const AdminAnnouncements: React.FC = () => {
  const { announcements, isLoadingAnnouncements, fetchAnnouncements, createAnnouncement, toggleAnnouncement, deleteAnnouncement } = useAdminStore();
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [type, setType] = useState<'info' | 'warning' | 'maintenance' | 'update'>('info');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => { fetchAnnouncements(); }, []);

  const handleCreate = async () => {
    if (!title.trim() || !content.trim()) return;
    const ok = await createAnnouncement({
      title, content, type,
      starts_at: startsAt || undefined,
      ends_at: endsAt || undefined,
    });
    if (ok) {
      setShowForm(false);
      setTitle(''); setContent(''); setType('info'); setStartsAt(''); setEndsAt('');
    }
  };

  const handleDelete = async (id: string) => {
    setDeleting(id);
    await deleteAnnouncement(id);
    setDeleting(null);
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-text-primary">Annonces système</h2>
            <p className="text-xs text-text-muted mt-0.5">Bannières affichées à tous les utilisateurs</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-admin-accent/10 text-admin-accent text-sm font-medium hover:bg-admin-accent/20 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" /> Nouvelle annonce
          </button>
        </div>

        {/* Create form */}
        {showForm && (
          <div className="bg-bg-secondary/60 rounded-xl border border-admin-accent/30 p-5 space-y-3">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titre de l'annonce"
              className="w-full px-3 py-2 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary placeholder-text-muted"
            />
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Contenu..."
              rows={3}
              className="w-full px-3 py-2 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary placeholder-text-muted resize-none"
            />
            <div className="flex gap-2">
              {(Object.entries(TYPE_CONFIG) as [string, typeof TYPE_CONFIG[string]][]).map(([k, v]) => {
                const Icon = v.icon;
                return (
                  <button
                    key={k}
                    onClick={() => setType(k as any)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs border transition-colors ${
                      type === k ? `${v.cls} border-current` : 'border-border-color text-text-muted hover:border-text-muted'
                    }`}
                  >
                    <Icon className="h-3 w-3" /> {v.label}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-[10px] text-text-muted uppercase tracking-wider mb-1 block">Début (optionnel)</label>
                <input
                  type="datetime-local"
                  value={startsAt}
                  onChange={(e) => setStartsAt(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary"
                />
              </div>
              <div className="flex-1">
                <label className="text-[10px] text-text-muted uppercase tracking-wider mb-1 block">Fin (optionnel)</label>
                <input
                  type="datetime-local"
                  value={endsAt}
                  onChange={(e) => setEndsAt(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowForm(false)} className="px-3 py-2 rounded-lg bg-bg-tertiary text-text-secondary text-sm">Annuler</button>
              <button onClick={handleCreate} disabled={!title.trim() || !content.trim()} className="px-4 py-2 rounded-lg bg-admin-accent text-white text-sm font-medium disabled:opacity-50">Publier</button>
            </div>
          </div>
        )}

        {/* List */}
        {isLoadingAnnouncements ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-admin-accent" />
          </div>
        ) : announcements.length === 0 ? (
          <div className="text-center py-16">
            <Megaphone className="h-10 w-10 text-text-muted/20 mx-auto mb-3" />
            <p className="text-sm text-text-muted">Aucune annonce système</p>
          </div>
        ) : (
          <div className="space-y-2">
            {announcements.map((ann) => {
              const cfg = TYPE_CONFIG[ann.type] || TYPE_CONFIG.info;
              const Icon = cfg.icon;
              return (
                <div key={ann.id} className={`rounded-xl border p-4 ${ann.is_active ? cfg.cls : 'bg-bg-secondary/30 border-border-color/30 opacity-60'}`}>
                  <div className="flex items-start gap-3">
                    <Icon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-medium">{ann.title}</h3>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${cfg.cls}`}>{cfg.label}</span>
                      </div>
                      <p className="text-xs mt-1 opacity-80">{ann.content}</p>
                      <p className="text-[10px] mt-2 opacity-50">
                        Par {ann.author_username} · {new Date(ann.created_at).toLocaleDateString('fr-CA')}
                        {ann.starts_at && ` · Début: ${new Date(ann.starts_at).toLocaleDateString('fr-CA')}`}
                        {ann.ends_at && ` · Fin: ${new Date(ann.ends_at).toLocaleDateString('fr-CA')}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => toggleAnnouncement(ann.id, !ann.is_active)}
                        className="p-1.5 rounded-lg hover:bg-black/10 transition-colors"
                        title={ann.is_active ? 'Désactiver' : 'Activer'}
                      >
                        {ann.is_active ? <ToggleRight className="h-5 w-5 text-success" /> : <ToggleLeft className="h-5 w-5 text-text-muted" />}
                      </button>
                      <button
                        onClick={() => handleDelete(ann.id)}
                        disabled={deleting === ann.id}
                        className="p-1.5 rounded-lg hover:bg-error/10 text-error/60 hover:text-error transition-colors disabled:opacity-50"
                      >
                        {deleting === ann.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnnouncements;
